import {
  Stack,
  Tab as MuiTab,
  type TabProps as MuiTabProps,
  Tabs as MuiTabs,
  type TabsProps as MuiTabsProps,
} from "@mui/material";

import { type TabVariant } from "../../../ui-theme";
import { Badge } from "../Badge";
import { Text } from "../Text";

type BaseTabProps = Omit<MuiTabProps, "appearance">;

type TabProps = BaseTabProps & {
  variant: TabVariant;
  count?: number;
};

export function Tab(props: TabProps) {
  const { variant, ...rest } = props;

  function getLabel(props: TabProps) {
    const { label, count, variant } = props;

    switch (variant) {
      case "pill": {
        return (
          <Stack direction="row" alignItems="center" spacing={8}>
            <Text semibold variant="body2">
              {label}
            </Text>
            {count ? <Badge count={count} color="primary" size="large" /> : null}
          </Stack>
        );
      }

      case "underlined": {
        return (
          <Stack direction="row" alignItems="center" spacing={5}>
            <Text semibold variant="body2" sx={{ color: "inherit" }}>
              {label}
            </Text>

            {Boolean(count) && (
              <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
                {count}
              </Text>
            )}
          </Stack>
        );
      }

      default: {
        return label;
      }
    }
  }

  return <MuiTab {...rest} appearance={variant} label={getLabel(props)} />;
}

export interface TabsProps extends Omit<MuiTabsProps, "variant" | "appearance"> {
  variant: TabVariant;
}

export function Tabs(props: TabsProps) {
  const { variant, ...rest } = props;

  return <MuiTabs {...rest} appearance={variant} variant="scrollable" scrollButtons={false} />;
}
